import React from 'react';
import { Box } from '@mui/material';

import { ComponentDesign, FeaturePatternTrussSettings } from '@paragon/contract-library';
import { AngleField, DistanceField } from '@paragon/material';

import { updateActiveFeaturePatternTrussSettings } from '../../../../../../store/actions/editorActions';
import { useAppDispatch, useAppSelector } from '../../../../../hooks';
import { ContinuouslyBearingSettings } from '../../../properties/ContinuouslyBearingSettings';
import {
  EndConditionSettingsPanel,
  InsertionComponentEndConditionSettingsPanel,
} from '../../../properties/EndConditionSettings';
import { ActiveComponentGableSettings, GableSettingsSubPanel } from '../../../properties/GableSettings';
import {
  ActiveComponentInteriorConditionSettingsProperties,
  InteriorConditionSettingsProperties,
} from '../../../properties/InteriorConditions/InteriorConditionSettingsProperties';
import { LabeledBox } from '../../../properties/LabeledBox';
import { Length } from './Length';

interface UncontrolledModeProps {
  forInsertionComponent: true;
}

interface ControlledModeProps {
  forInsertionComponent?: false;

  activeComponentDesign?: ComponentDesign;

  value: FeaturePatternTrussSettings;
  onChange(newValue: FeaturePatternTrussSettings): void;
}

export function FeaturePatternTrussShapeDetails(props: UncontrolledModeProps | ControlledModeProps) {
  const onChange =
    <K extends keyof FeaturePatternTrussSettings>(key: K) =>
    (value: FeaturePatternTrussSettings[K]) => {
      if (props.forInsertionComponent)
        throw new Error('FeaturePatternTrussSettings onChange called in uncontrolled mode.');

      props.onChange({ ...props.value, [key]: value });
    };

  return (
    <>
      <Box sx={{ mb: 2 }}>
        {props.forInsertionComponent ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Length />
              <InsertionDepth />
            </Box>
            <InsertionSlopes />
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <DistanceField label="Length" value={props.value.width} onSubmit={onChange('width')} sx={textFieldSx} />
              <DistanceField label="Depth" value={props.value.depth} onSubmit={onChange('depth')} sx={textFieldSx} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
              <AngleField
                label="Top Chord Slope"
                defaultDisplayFormat="slope"
                value={props.value.topChordSlope}
                onSubmit={onChange('topChordSlope')}
                sx={{ mx: 1 }}
              />
              <AngleField
                label="Bottom Chord Slope"
                defaultDisplayFormat="slope"
                value={props.value.bottomChordSlope}
                onSubmit={onChange('bottomChordSlope')}
                sx={{ mx: 1 }}
              />
            </Box>
          </>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
        <LabeledBox title="Left End">
          {props.forInsertionComponent ? (
            <InsertionComponentEndConditionSettingsPanel side="Left" />
          ) : (
            <EndConditionSettingsPanel
              defaultPattern="advancedSettings"
              side="Left"
              value={props.value.leftEndSettings}
              onChange={onChange('leftEndSettings')}
            />
          )}
        </LabeledBox>

        <LabeledBox title="Right End">
          {props.forInsertionComponent ? (
            <InsertionComponentEndConditionSettingsPanel side="Right" />
          ) : (
            <EndConditionSettingsPanel
              defaultPattern="advancedSettings"
              side="Right"
              value={props.value.rightEndSettings}
              onChange={onChange('rightEndSettings')}
            />
          )}
        </LabeledBox>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
        <LabeledBox title="Interior Conditions">
          {props.forInsertionComponent ? (
            <ActiveComponentInteriorConditionSettingsProperties />
          ) : (
            <InteriorConditionSettingsProperties
              values={props.value.interiorConditionSettings}
              onChange={onChange('interiorConditionSettings')}
            />
          )}
        </LabeledBox>
      </Box>

      <LabeledBox title="General Settings">
        {props.forInsertionComponent ? (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <ActiveComponentGableSettings showContinuouslyBearing />
          </Box>
        ) : (
          <>
            <ContinuouslyBearingSettings
              disabled={false}
              value={props.value.continuouslyBearing}
              onChange={onChange('continuouslyBearing')}
            />
            <GableSettingsSubPanel value={props.value.gableSettings} onChange={onChange('gableSettings')} />
          </>
        )}
      </LabeledBox>
    </>
  );
}

const textFieldSx = { m: 1, flex: 1 } as const;

function InsertionDepth() {
  const dispatch = useAppDispatch();

  const insertingObjectInfo = useAppSelector((state) => state.application.insertingObjectInfo);

  if (insertingObjectInfo?.type !== 'componentDesign') throw new Error('Expected to be inserting a component');

  const settings = insertingObjectInfo.designSettings;
  if (settings.definitionType !== 'FeaturePattern') throw new Error('Expected to be inserting a profile truss');

  const onChangeFeaturePatternSetting =
    <K extends keyof FeaturePatternTrussSettings>(key: K) =>
    (value: FeaturePatternTrussSettings[K]) => {
      if (settings[key] !== value) {
        dispatch(updateActiveFeaturePatternTrussSettings({ ...settings, [key]: value }));
      }
    };

  return (
    <DistanceField
      label="Depth"
      value={settings.depth}
      onSubmit={onChangeFeaturePatternSetting('depth')}
      sx={textFieldSx}
    />
  );
}

function InsertionSlopes() {
  const dispatch = useAppDispatch();

  const insertingObjectInfo = useAppSelector((state) => state.application.insertingObjectInfo);

  if (insertingObjectInfo?.type !== 'componentDesign') throw new Error('Expected to be inserting a component');

  const settings = insertingObjectInfo.designSettings;
  if (settings.definitionType !== 'FeaturePattern') throw new Error('Expected to be inserting a profile truss');

  const onChangeFeaturePatternSetting =
    <K extends keyof FeaturePatternTrussSettings>(key: K) =>
    (value: FeaturePatternTrussSettings[K]) => {
      if (settings[key] !== value) {
        dispatch(updateActiveFeaturePatternTrussSettings({ ...settings, [key]: value }));
      }
    };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
      <AngleField
        label="Top Chord Slope"
        defaultDisplayFormat="slope"
        value={settings.topChordSlope}
        onSubmit={onChangeFeaturePatternSetting('topChordSlope')}
        sx={{ mx: 1 }}
      />
      <AngleField
        label="Bottom Chord Slope"
        defaultDisplayFormat="slope"
        value={settings.bottomChordSlope}
        onSubmit={onChangeFeaturePatternSetting('bottomChordSlope')}
        sx={{ mx: 1 }}
      />
    </Box>
  );
}
